import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Store, Plus, Edit, Trash2, TrendingUp, DollarSign, 
  Star, Package, Leaf, MapPin, X, Upload, Download, FileText 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const VendorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [meals, setMeals] = useState([]);
  const [showAddMealModal, setShowAddMealModal] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  
  // CSV Upload state
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [csvFile, setCSVFile] = useState(null);
  const [csvPreview, setCSVPreview] = useState(null);
  const [csvLoading, setCSVLoading] = useState(false);

  const [mealForm, setMealForm] = useState({
    name: '',
    description: '',
    price: '',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    cuisine_type: 'western',
    meal_type: 'breakfast',
    category: 'balanced',
    ingredients_type: 'mixed',
    tags: [],
    dietary_labels: [],
    allergens: [],
    benefits: [],
    is_farm_fresh: false,
    is_locally_sourced: false,
    is_organic: false,
    ingredients_source: '',
    prep_time: '20',
    portions_available: '50'
  });

  const cuisineOptions = [
    'west_african', 'east_african', 'caribbean', 'western',
    'middle_eastern', 'south_asian', 'east_asian', 'southeast_asian',
    'latin_american', 'mediterranean'
  ];

  const mealTypes = ['breakfast', 'lunch', 'dinner', 'snacks'];
  const categories = ['balanced', 'protein_rich', 'light', 'heavy', 'vegetable_based', 'quick'];
  const ingredientsTypes = ['protein', 'grain', 'vegetable', 'mixed'];

  const tagOptions = [
    'vegan', 'vegetarian', 'gluten_free', 'dairy_free', 'high_protein',
    'low_carb', 'traditional', 'modern', 'spicy', 'mild'
  ];

  const dietaryLabelOptions = [
    'keto', 'paleo', 'low_carb', 'high_fiber', 'heart_healthy',
    'diabetic_friendly', 'anti_inflammatory'
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, mealsRes] = await Promise.all([
        axios.get(`${API}/vendors/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/vendors/meals`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setMeals(mealsRes.data.meals || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Please log in as a vendor');
        navigate('/vendor-login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMealForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleTag = (tag, field) => {
    setMealForm(prev => ({
      ...prev,
      [field]: prev[field].includes(tag)
        ? prev[field].filter(t => t !== tag)
        : [...prev[field], tag]
    }));
  };

  const handleCreateMeal = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${API}/vendors/meals`, {
        ...mealForm,
        price: parseFloat(mealForm.price),
        calories: parseInt(mealForm.calories),
        protein: parseInt(mealForm.protein),
        carbs: parseInt(mealForm.carbs),
        fats: parseInt(mealForm.fats),
        prep_time: parseInt(mealForm.prep_time),
        portions_available: parseInt(mealForm.portions_available),
        benefits: mealForm.benefits.filter(b => b.trim() !== '')
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success('Meal created successfully!');
        setShowAddMealModal(false);
        resetMealForm();
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error creating meal:', error);
      toast.error(error.response?.data?.detail || 'Failed to create meal');
    }
  };

  const handleDeleteMeal = async (mealId) => {
    if (!window.confirm('Are you sure you want to delete this meal?')) return;

    try {
      await axios.delete(`${API}/vendors/meals/${mealId}`, { withCredentials: true });
      toast.success('Meal deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting meal:', error);
      toast.error('Failed to delete meal');
    }
  };

  const handleToggleAvailability = async (meal) => {
    try {
      await axios.patch(`${API}/vendors/meals/${meal.id}`, {
        available: !meal.available
      }, { withCredentials: true });

      toast.success(`Meal ${!meal.available ? 'activated' : 'deactivated'}`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast.error('Failed to update meal');
    }
  };

  const resetMealForm = () => {
    setMealForm({
      name: '',
      description: '',
      price: '',
      calories: '',
      protein: '',
      carbs: '',
      fats: '',
      cuisine_type: 'western',
      meal_type: 'breakfast',
      category: 'balanced',
      ingredients_type: 'mixed',
      tags: [],
      dietary_labels: [],
      allergens: [],
      benefits: [],
      is_farm_fresh: false,
      is_locally_sourced: false,
      is_organic: false,
      ingredients_source: '',
      prep_time: '20',
      portions_available: '50'
    });
  };

  // CSV Upload handlers
  const handleCSVDownload = async () => {
    try {
      const response = await axios.get(`${API}/vendors/meals/csv-template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'meal_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setCSVFile(file);
    setCSVLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await axios.post(`${API}/vendors/meals/upload-csv`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCSVPreview(data);
      
      if (data.errors && data.errors.length > 0) {
        toast.warning(`Parsed with ${data.errors.length} errors. Review before importing.`);
      } else {
        toast.success(`Successfully parsed ${data.meals_count} meals!`);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse CSV file');
      setCSVFile(null);
    } finally {
      setCSVLoading(false);
    }
  };

  const handleCSVImport = async () => {
    if (!csvPreview || !csvPreview.meals_preview) return;
    
    setCSVLoading(true);
    
    try {
      const { data } = await axios.post(`${API}/vendors/meals/import-csv`, csvPreview.meals_preview, {
        withCredentials: true
      });
      
      toast.success(data.message);
      
      // Refresh meals list
      await fetchDashboardData();
      
      // Reset CSV state
      setShowCSVUpload(false);
      setCSVFile(null);
      setCSVPreview(null);
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to import meals');
    } finally {
      setCSVLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Store className="h-8 w-8 mr-3 text-green-600" />
              Vendor Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Manage your meals and track performance</p>
          </div>
          <Button onClick={() => navigate('/dashboard')}>
            User Dashboard
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Meals</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_meals}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.active_meals} active</p>
                  </div>
                  <Package className="h-10 w-10 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Orders</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_orders}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.pending_orders} pending</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Today's Revenue</p>
                    <p className="text-3xl font-bold text-gray-900">
                      ${stats.revenue_today.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Last 24 hours</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Average Rating</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.avg_rating.toFixed(1)}</p>
                    <div className="flex items-center mt-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      <span className="text-xs text-gray-500 ml-1">Customer reviews</span>
                    </div>
                  </div>
                  <Star className="h-10 w-10 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6">
          <Button
            onClick={() => setShowAddMealModal(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Meal
          </Button>
          
          <Button
            onClick={() => setShowCSVUpload(!showCSVUpload)}
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload CSV
          </Button>
        </div>

        {/* CSV Upload Section */}
        {showCSVUpload && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center text-green-900">
                <FileText className="h-5 w-5 mr-2" />
                Bulk Upload Meals via CSV
              </CardTitle>
              <CardDescription>Upload multiple meals at once using our CSV template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Download Template */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
                <div>
                  <p className="font-medium text-gray-900">Step 1: Download CSV Template</p>
                  <p className="text-sm text-gray-600">Get the template with example meals and required format</p>
                </div>
                <Button onClick={handleCSVDownload} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {/* Upload CSV */}
              <div className="p-4 bg-white rounded-lg border border-green-200">
                <p className="font-medium text-gray-900 mb-3">Step 2: Upload Your CSV File</p>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                  {csvLoading && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                  )}
                </div>
                {csvFile && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: <span className="font-medium">{csvFile.name}</span>
                  </p>
                )}
              </div>

              {/* Preview & Errors */}
              {csvPreview && (
                <>
                  <div className="p-4 bg-white rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-gray-900">Preview ({csvPreview.meals_count} meals)</p>
                        <p className="text-sm text-gray-600">
                          Showing first {Math.min(10, csvPreview.meals_count)} meals
                        </p>
                      </div>
                      {csvPreview.errors && csvPreview.errors.length > 0 && (
                        <Badge variant="destructive">
                          {csvPreview.errors.length} errors
                        </Badge>
                      )}
                    </div>

                    {/* Errors List */}
                    {csvPreview.errors && csvPreview.errors.length > 0 && (
                      <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="font-semibold text-red-900 mb-2">Validation Errors:</p>
                        <ul className="text-sm text-red-700 space-y-1">
                          {csvPreview.errors.slice(0, 10).map((error, idx) => (
                            <li key={idx}>• {error}</li>
                          ))}
                          {csvPreview.errors.length > 10 && (
                            <li className="text-red-600 font-medium">
                              ... and {csvPreview.errors.length - 10} more errors
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Preview Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 text-left">Name</th>
                            <th className="p-2 text-left">Calories</th>
                            <th className="p-2 text-left">Protein</th>
                            <th className="p-2 text-left">Cuisine</th>
                            <th className="p-2 text-left">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.meals_preview?.map((meal, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2">{meal.emoji} {meal.name}</td>
                              <td className="p-2">{meal.calories}</td>
                              <td className="p-2">{meal.protein}g</td>
                              <td className="p-2 capitalize">{meal.cuisine.replace(/_/g, ' ')}</td>
                              <td className="p-2">${meal.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Import Button */}
                  <div className="flex gap-4">
                    <Button
                      onClick={handleCSVImport}
                      disabled={csvLoading || (csvPreview.errors && csvPreview.errors.length > 0)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {csvLoading ? 'Importing...' : `Import ${csvPreview.meals_count} Meals`}
                    </Button>
                    <Button
                      onClick={() => {
                        setCSVFile(null);
                        setCSVPreview(null);
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Meals Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Your Meals</CardTitle>
                <CardDescription>Manage your menu and meal availability</CardDescription>
              </div>
              <Button onClick={() => setShowAddMealModal(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />
                Add New Meal
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {meals.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 mb-4">No meals listed yet</p>
                <Button onClick={() => setShowAddMealModal(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Meal
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {meals.map(meal => (
                  <Card key={meal.id} className={`${!meal.available ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{meal.name}</h3>
                        <Badge className={meal.available ? 'bg-green-600' : 'bg-gray-400'}>
                          {meal.available ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>

                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{meal.description}</p>

                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {meal.is_farm_fresh && (
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                            🌾 Farm Fresh
                          </Badge>
                        )}
                        {meal.is_locally_sourced && (
                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                            🏘️ Local
                          </Badge>
                        )}
                        {meal.is_organic && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800">
                            🌿 Organic
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          ⭐ {(meal.ai_match_score * 100).toFixed(0)}% Match
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <span className="text-gray-600">Price:</span>
                          <span className="font-semibold ml-1">${meal.price.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Stock:</span>
                          <span className="font-semibold ml-1">{meal.portions_available}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Calories:</span>
                          <span className="font-semibold ml-1">{meal.calories}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Protein:</span>
                          <span className="font-semibold ml-1">{meal.protein}g</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleAvailability(meal)}
                          className="flex-1"
                        >
                          {meal.available ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteMeal(meal.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Meal Modal */}
        {showAddMealModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="max-w-3xl w-full my-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Add New Meal</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setShowAddMealModal(false);
                    resetMealForm();
                  }}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="max-h-[70vh] overflow-y-auto">
                <form onSubmit={handleCreateMeal} className="space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold border-b pb-2">Basic Information</h3>
                    <div>
                      <Label htmlFor="name">Meal Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={mealForm.name}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Grilled Chicken Salad"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={mealForm.description}
                        onChange={handleInputChange}
                        required
                        rows={3}
                        placeholder="Describe your meal..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="price">Price ($) *</Label>
                        <Input
                          id="price"
                          name="price"
                          type="number"
                          step="0.01"
                          value={mealForm.price}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="prep_time">Prep Time (min) *</Label>
                        <Input
                          id="prep_time"
                          name="prep_time"
                          type="number"
                          value={mealForm.prep_time}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Nutrition */}
                  <div className="space-y-4">
                    <h3 className="font-semibold border-b pb-2">Nutritional Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="calories">Calories *</Label>
                        <Input
                          id="calories"
                          name="calories"
                          type="number"
                          value={mealForm.calories}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="protein">Protein (g) *</Label>
                        <Input
                          id="protein"
                          name="protein"
                          type="number"
                          value={mealForm.protein}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="carbs">Carbs (g) *</Label>
                        <Input
                          id="carbs"
                          name="carbs"
                          type="number"
                          value={mealForm.carbs}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="fats">Fats (g) *</Label>
                        <Input
                          id="fats"
                          name="fats"
                          type="number"
                          value={mealForm.fats}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Categorization */}
                  <div className="space-y-4">
                    <h3 className="font-semibold border-b pb-2">Categorization</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="cuisine_type">Cuisine Type *</Label>
                        <select
                          id="cuisine_type"
                          name="cuisine_type"
                          value={mealForm.cuisine_type}
                          onChange={handleInputChange}
                          className="w-full h-10 px-3 border border-gray-300 rounded-md"
                        >
                          {cuisineOptions.map(c => (
                            <option key={c} value={c}>{c.replace('_', ' ')}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="meal_type">Meal Type *</Label>
                        <select
                          id="meal_type"
                          name="meal_type"
                          value={mealForm.meal_type}
                          onChange={handleInputChange}
                          className="w-full h-10 px-3 border border-gray-300 rounded-md"
                        >
                          {mealTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="category">Category *</Label>
                        <select
                          id="category"
                          name="category"
                          value={mealForm.category}
                          onChange={handleInputChange}
                          className="w-full h-10 px-3 border border-gray-300 rounded-md"
                        >
                          {categories.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="ingredients_type">Ingredients Type *</Label>
                        <select
                          id="ingredients_type"
                          name="ingredients_type"
                          value={mealForm.ingredients_type}
                          onChange={handleInputChange}
                          className="w-full h-10 px-3 border border-gray-300 rounded-md"
                        >
                          {ingredientsTypes.map(i => <option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-4">
                    <h3 className="font-semibold border-b pb-2">Tags</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {tagOptions.map(tag => (
                        <div
                          key={tag}
                          className={`border-2 rounded p-2 cursor-pointer text-sm ${
                            mealForm.tags.includes(tag) ? 'border-green-500 bg-green-50' : 'border-gray-200'
                          }`}
                          onClick={() => toggleTag(tag, 'tags')}
                        >
                          <Checkbox checked={mealForm.tags.includes(tag)} />
                          <span className="ml-2">{tag.replace('_', ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Dietary Labels */}
                  <div className="space-y-4">
                    <h3 className="font-semibold border-b pb-2">Dietary Labels</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {dietaryLabelOptions.map(label => (
                        <div
                          key={label}
                          className={`border-2 rounded p-2 cursor-pointer text-sm ${
                            mealForm.dietary_labels.includes(label) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}
                          onClick={() => toggleTag(label, 'dietary_labels')}
                        >
                          <Checkbox checked={mealForm.dietary_labels.includes(label)} />
                          <span className="ml-2">{label.replace('_', ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sourcing */}
                  <div className="space-y-4">
                    <h3 className="font-semibold border-b pb-2">Sourcing & Sustainability</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3 p-2 border-2 border-green-200 rounded bg-green-50">
                        <Checkbox
                          checked={mealForm.is_farm_fresh}
                          onCheckedChange={(checked) => setMealForm(prev => ({ ...prev, is_farm_fresh: checked }))}
                        />
                        <Label>Farm Fresh 🌾</Label>
                      </div>
                      <div className="flex items-center space-x-3 p-2 border-2 border-blue-200 rounded bg-blue-50">
                        <Checkbox
                          checked={mealForm.is_locally_sourced}
                          onCheckedChange={(checked) => setMealForm(prev => ({ ...prev, is_locally_sourced: checked }))}
                        />
                        <Label>Locally Sourced 🏘️</Label>
                      </div>
                      <div className="flex items-center space-x-3 p-2 border-2 border-purple-200 rounded bg-purple-50">
                        <Checkbox
                          checked={mealForm.is_organic}
                          onCheckedChange={(checked) => setMealForm(prev => ({ ...prev, is_organic: checked }))}
                        />
                        <Label>Organic 🌿</Label>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="ingredients_source">Ingredients Source (optional)</Label>
                      <Input
                        id="ingredients_source"
                        name="ingredients_source"
                        value={mealForm.ingredients_source}
                        onChange={handleInputChange}
                        placeholder="e.g., Hudson Valley Farms"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddMealModal(false);
                        resetMealForm();
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                      Create Meal
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDashboard;
